import React,{useMemo,useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import FolderTree from '../Folders/FolderTree';
import AddFolderModal from '../Modals/AddFolderModal';
import MobileLibraryBrowser from './MobileLibraryBrowser';
import {useTheme} from '../../contexts/ThemeContext';
import {useBookmarks} from '../../contexts/BookmarkContext';
import {useSettings} from '../../contexts/SettingsContext';

const {FiHome,FiFolderPlus,FiFolder,FiSearch,FiStar,FiActivity,FiX}=FiIcons;

const urlKey=(value)=> {
  try {
    const url=new URL(value);
    url.hash='';
    url.hostname=url.hostname.toLowerCase().replace(/^www\./,'');
    url.pathname=url.pathname.replace(/\/$/,'');
    return `${url.hostname}${url.pathname}${url.search}`.toLowerCase();
  } catch {return String(value || '').trim().toLowerCase();}
};

const Sidebar=({isCollapsed=true,onClose,isMobile=false})=> {
  const [showAddFolder,setShowAddFolder]=useState(false);
  const [editingFolder,setEditingFolder]=useState(null);
  const {theme}=useTheme();
  const {settings,deleteSmartCollection}=useSettings();
  const {bookmarks,folders,activeFolderId,setCurrentFolder,selectedFolders,setSelectedFolders,updateFolder,getBookmarkCount,search}=useBookmarks();
  const isCollapsedState=isCollapsed && !isMobile;

  const healthIssueCount=useMemo(()=> {
    const seen=new Map();
    let issues=0;
    (bookmarks || []).forEach(bookmark=> {
      if (!/^https?:\/\//i.test(bookmark.url || '')) issues+=1;
      if (!bookmark.faviconfile && !bookmark.faviconurl) issues+=1;
      const modified=new Date(bookmark.dateModified || bookmark.dateAdded || 0).getTime();
      if (modified && Date.now()-modified>180*24*60*60*1000) issues+=1;
      const key=urlKey(bookmark.url);
      seen.set(key,(seen.get(key) || 0)+1);
    });
    for (const count of seen.values()) if (count>1) issues+=count-1;
    return issues;
  },[bookmarks]);

  if (isMobile) return <MobileLibraryBrowser onClose={onClose} />;

  const handleLibraryView=(id)=> {
    search('');
    setCurrentFolder(id);
  };

  const renderNavItem=(icon,label,isActive,onClick,count=0)=> (
    <button
      onClick={onClick}
      className={`group relative flex items-center w-full min-h-11 rounded-2xl px-4 py-3 transition-colors duration-200 ${isActive ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30' : theme==='dark' ? 'hover:bg-zinc-800 text-zinc-400 hover:text-white' : 'hover:bg-gray-100 text-zinc-600 hover:text-indigo-600'} ${isCollapsedState ? 'justify-center px-0' : 'space-x-3'}`}
    >
      <SafeIcon icon={icon} className={`${isCollapsedState ? 'w-6 h-6' : 'w-5 h-5'} flex-shrink-0`} />
      {!isCollapsedState && (
        <>
          <span className="flex-1 text-sm font-black uppercase tracking-wider truncate text-left">{label}</span>
          {count>0 && <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${isActive ? 'bg-white/20' : theme==='dark' ? 'bg-zinc-800 text-zinc-500' : 'bg-gray-200 text-zinc-500'}`}>{count}</span>}
        </>
      )}
      {isCollapsedState && count>0 && <div className="absolute top-2 right-2 w-2 h-2 bg-indigo-500 rounded-full" />}
      {isCollapsedState && <div className="absolute left-full ml-4 px-3 py-2 bg-zinc-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-[100] shadow-2xl">{label}{count ? ` (${count})` : ''}</div>}
    </button>
  );

  return (
    <aside className={`flex flex-col h-full min-h-0 transition-colors duration-300 ${theme==='dark' ? 'bg-slate-900' : 'bg-white'}`}>
      <div className={`p-5 sm:p-6 bg-inherit z-20 flex-shrink-0 ${isCollapsedState ? 'flex flex-col items-center' : 'flex items-center justify-between'}`}>
        {!isCollapsedState ? (
          <>
            <div className="space-y-1"><h2 className="text-xl font-black tracking-tighter">LIBRARY</h2><div className="w-8 h-1 bg-indigo-600 rounded-full" /></div>
            <button onClick={()=> setShowAddFolder(true)} className="p-2.5 bg-indigo-600 text-white rounded-xl shadow-lg shadow-indigo-500/20 active:bg-indigo-700" title="New Folder" aria-label="New folder"><SafeIcon icon={FiFolderPlus} className="w-5 h-5" /></button>
          </>
        ) : (
          <button onClick={()=> setShowAddFolder(true)} className="p-3 bg-indigo-600 text-white rounded-2xl shadow-lg shadow-indigo-500/20" title="New Folder" aria-label="New folder"><SafeIcon icon={FiFolderPlus} className="w-6 h-6" /></button>
        )}
      </div>

      <div className="flex-1 px-3 space-y-6 overflow-y-auto custom-scrollbar overscroll-contain min-h-0 pb-[max(1.5rem,env(safe-area-inset-bottom,24px))]">
        <div className="space-y-1">
          {!isCollapsedState && <p className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 px-3 mb-3">Main View</p>}
          {renderNavItem(FiHome,'All Library',activeFolderId==='ALL',()=> handleLibraryView('ALL'),getBookmarkCount('ALL'))}
          {renderNavItem(FiStar,'Unfiled',activeFolderId==='UNFILED',()=> handleLibraryView('UNFILED'),getBookmarkCount('UNFILED'))}
          {renderNavItem(FiActivity,'Library Health',activeFolderId==='HEALTH',()=> handleLibraryView('HEALTH'),healthIssueCount)}
        </div>

        {(settings.smartCollections || []).length>0 && (
          <div className="space-y-1">
            {!isCollapsedState && <p className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 px-3 mb-3">Smart Collections</p>}
            {(settings.smartCollections || []).map(collection=> {
              const active=activeFolderId===`SMART:${collection.id}`;
              if (isCollapsedState) return <React.Fragment key={collection.id}>{renderNavItem(FiSearch,collection.name,active,()=> handleLibraryView(`SMART:${collection.id}`))}</React.Fragment>;
              return (
                <div key={collection.id} className="group/smart relative">
                  <button onClick={()=> handleLibraryView(`SMART:${collection.id}`)} className={`flex items-center w-full min-h-11 rounded-2xl pl-4 pr-12 py-3 space-x-3 transition-colors ${active ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30' : theme==='dark' ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-gray-100 text-zinc-600'}`}>
                    <SafeIcon icon={FiSearch} className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-black uppercase tracking-wider truncate">{collection.name}</span>
                  </button>
                  <button
                    onClick={async event=> {event.stopPropagation();await deleteSmartCollection(collection.id);if (active) setCurrentFolder('ALL');}}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-xl opacity-70 sm:opacity-0 sm:group-hover/smart:opacity-100 ${active ? 'text-white hover:bg-white/10' : 'text-zinc-400 hover:bg-zinc-500/10'}`}
                    aria-label={`Delete ${collection.name}`}
                  ><SafeIcon icon={FiX} className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        )}

        <div className="space-y-2">
          {!isCollapsedState && <div className="flex items-center justify-between px-3 mb-3"><p className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500">Folders</p></div>}
          <FolderTree
            folders={folders}
            currentFolder={activeFolderId}
            onFolderSelect={handleLibraryView}
            onEditFolder={setEditingFolder}
            onUpdateFolder={updateFolder}
            onDeleteFolder={()=> {}}
            getBookmarkCount={getBookmarkCount}
            selectedFolders={selectedFolders}
            onSelectFolder={setSelectedFolders}
            isCollapsed={isCollapsedState}
          />
        </div>

        {!isCollapsedState && folders.length===0 && (
          <div className="p-5 text-center bg-zinc-50 dark:bg-zinc-900/50 rounded-3xl border border-dashed dark:border-zinc-800">
            <div className="w-12 h-12 bg-zinc-100 dark:bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-3"><SafeIcon icon={FiFolder} className="w-6 h-6 text-zinc-400" /></div>
            <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-4">Create your first folder</p>
            <button onClick={()=> setShowAddFolder(true)} className="px-4 py-2 bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl">New Folder</button>
          </div>
        )}
      </div>

      <AddFolderModal isOpen={showAddFolder || !!editingFolder} onClose={()=> {setShowAddFolder(false);setEditingFolder(null);}} editFolder={editingFolder} />
    </aside>
  );
};

export default Sidebar;